import { FormEvent, useState } from 'react';

export type AccountPaginationMeta = {
  Page: number;
  PerPage: number;
  Total: number;
  TotalPages: number;
};

type AccountPaginationProps = {
  pagination: AccountPaginationMeta;
  disabled?: boolean;
  onChange: (page: number) => void;
};

function visiblePages(current: number, total: number) {
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  return Array.from(pages)
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);
}

export default function AccountPagination({ pagination, disabled = false, onChange }: AccountPaginationProps) {
  const { Page, Total, TotalPages } = pagination;
  const pages = visiblePages(Page, TotalPages);
  const [jumpValue, setJumpValue] = useState('');

  if (Total === 0 || TotalPages <= 1) return null;

  function handleJumpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsed = parseInt(jumpValue, 10);
    if (!Number.isFinite(parsed)) return;
    const target = Math.min(Math.max(1, parsed), TotalPages);
    if (target !== Page) onChange(target);
    setJumpValue('');
  }

  return (
    <div className="mt-6 flex flex-col items-center gap-3 border-t border-[#E8E0D6] pt-5 sm:flex-row sm:justify-between">
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-[#E8E0D6] bg-white px-3 text-sm font-medium text-[#1B3D2A] transition hover:bg-[#FAF8F5] disabled:cursor-not-allowed disabled:opacity-40"
          disabled={disabled || Page <= 1}
          onClick={() => onChange(Page - 1)}
          type="button"
        >
          <i className="ri-arrow-left-s-line" />
          上一頁
        </button>

        {pages.map((page, index) => {
          const previous = pages[index - 1];
          const hasGap = previous && page - previous > 1;
          return (
            <span className="inline-flex items-center gap-1.5" key={page}>
              {hasGap && <span className="px-1 text-sm text-[#9A8A7A]">...</span>}
              <button
                className={`h-9 min-w-9 rounded-lg border px-3 text-sm font-medium transition ${
                  page === Page
                    ? 'border-[#1B3D2A] bg-[#1B3D2A] text-white'
                    : 'border-[#E8E0D6] bg-white text-[#1B3D2A] hover:bg-[#FAF8F5]'
                }`}
                disabled={disabled || page === Page}
                onClick={() => onChange(page)}
                type="button"
              >
                {page}
              </button>
            </span>
          );
        })}

        <button
          className="inline-flex h-9 items-center gap-1 rounded-lg border border-[#E8E0D6] bg-white px-3 text-sm font-medium text-[#1B3D2A] transition hover:bg-[#FAF8F5] disabled:cursor-not-allowed disabled:opacity-40"
          disabled={disabled || Page >= TotalPages}
          onClick={() => onChange(Page + 1)}
          type="button"
        >
          下一頁
          <i className="ri-arrow-right-s-line" />
        </button>
      </div>

      {TotalPages > 5 && (
        <form className="flex items-center gap-1.5" onSubmit={handleJumpSubmit}>
          <label className="text-sm text-[#9A8A7A]" htmlFor="account-pagination-jump">跳至</label>
          <input
            id="account-pagination-jump"
            className="h-9 w-14 rounded-lg border border-[#E8E0D6] px-2 text-center text-sm outline-none focus:border-[#1B3D2A]"
            disabled={disabled}
            inputMode="numeric"
            max={TotalPages}
            min={1}
            placeholder={String(Page)}
            type="number"
            value={jumpValue}
            onChange={(event) => setJumpValue(event.target.value)}
          />
          <span className="whitespace-nowrap text-sm text-[#9A8A7A]">/ {TotalPages} 頁</span>
          <button
            className="inline-flex h-9 items-center justify-center rounded-lg border border-[#E8E0D6] bg-white px-3 text-sm font-medium text-[#1B3D2A] transition hover:bg-[#FAF8F5] disabled:cursor-not-allowed disabled:opacity-40"
            disabled={disabled || !jumpValue}
            type="submit"
          >
            前往
          </button>
        </form>
      )}
    </div>
  );
}
